import Splide from '@splidejs/splide';
import { isMobile } from '../../scripts/shared/utils';
import { elementsOf } from '@shared/easyfy.core';

export default class SliderHelper {
  static CreateSlider = (list: HTMLElement): { slider: Splide; excludePagination: boolean } => {
    if (!list) {
      console.error('CreateSlider: list element is null or undefined.');
      return null;
    }

    const articleCount = Number.parseInt(list.getAttribute('data-take') || '0', 10) || 0;

    // Get the container's width
    const containerWidth = list.clientWidth;

    // Calculate the total width required for the cards
    const cardWidth = 270; // Fixed width of each card
    const defaultGap = 32; // Default gap between cards
    const totalCardsWidth = articleCount > 0 ? articleCount * cardWidth + (articleCount - 1) * defaultGap : 0;

    // Determine the slider type and whether to exclude pagination
    const sliderType = totalCardsWidth < containerWidth ? 'slide' : 'loop';
    const excludePagination = totalCardsWidth < containerWidth;

    // Create the slider instance (unmounted)
    const slider = new Splide(list, {
      type: sliderType,
      perMove: 1,
      speed: 300,
      dragMinThreshold: 20,
      flickMaxPages: 1,
      flickPower: 100,
      snap: false,
      arrows: sliderType !== 'slide',
      gap: defaultGap,
      fixedWidth: cardWidth,
      padding: {
        left: 0,
        right: 0,
      },
      breakpoints: {
        1100: {
          gap: 24,
          arrows: false,
        },
        768: {
          gap: 12,
          arrows: false,
        },
      },
    });

    // Return the slider along with the excludePagination flag
    return { slider, excludePagination };
  };

  static OnPaginationMounted = (slider: Splide, list: HTMLElement, excludePagination: boolean = false) => {
    if (!excludePagination) {
      slider.on('pagination:mounted', (data) => {
        // if fewer slides than provided in options, hide pagination
        if (data.items.length <= slider.options.perPage) {
          slider.options.type = 'slide';
          slider.options.pagination = false;
          slider.options.drag = false;
          list.classList.add('hide-arrows');
        }
        data.list.classList.add('with-lines');
      });
    }
  };

  static PreventLinkMulitplication = (slider: Splide) => {
    // Splide.js creat copies of cards to make the carousel infinite.
    // To avoid link multiplication, we have to remove href from the invisible card's copies

    let hrefMap = this.createHrefMap(slider);

    this.removeHrefOfInvisibleCardCopies(slider.Components.Slides);

    slider.on('hidden', (s: any) => {
      // we need to check if the card is really hidden
      if (s.slideIndex != -1) {
        s.slide.querySelectorAll('a').forEach((el) => el.removeAttribute('href'));
      }
    });

    slider.on('visible', (s: any) => {
      if (s.index === 0) {
        // carousel get back to the first slide - we have to update all visible cards
        this.removeHrefOfInvisibleCardCopies(slider.Components.Slides);
        this.updateHrefOfVisibleCards(slider.Components.Slides, hrefMap);
      } else {
        let slide = s.slide;
        let nextSlide = slide.nextElementSibling;

        this.updateHrefInAnchors(slide, hrefMap);

        // Additionaly, we have to update the next slide since if we scroll to the right
        // there is a part of card that is visible and clickable
        // however in some cases it doesn't fire this event, so it doesn't get updated
        this.updateHrefInAnchors(nextSlide, hrefMap);
      }
    });
  };

  static AddSliderOverlay = (list: HTMLElement) => {
    const sliderOverlayEl = document.createElement('div');
    sliderOverlayEl.classList.add('slider-overlay');
    list.appendChild(sliderOverlayEl);
  };

  static UpdateImagesSize = (list: HTMLElement) => {
    if (isMobile) {
      const aEl = elementsOf(list, 'a:not([tabindex="-1"])');
      if (aEl) {
        const img = elementsOf(aEl, 'img.prod-card-img');
        if (img) {
          if (img.complete) {
            this.setImagesSize(list, img);
          } else {
            img.onload = () => {
              this.setImagesSize(list, img);
            };
          }
        }
      }
    }
  };

  private static setImagesSize = (list: HTMLElement, img: HTMLImageElement) => {
    const imgWidth = img.width;
    const imgHeight = img.height;

    list.querySelectorAll('img.prod-card-img').forEach((imgEl) => {
      imgEl.setAttribute('width', imgWidth.toString());
      imgEl.setAttribute('height', imgHeight.toString());
      imgEl.classList.add('horizontal-scroll');
    });
  };

  private static createHrefMap = (slider: Splide) => {
    var hrefMap: SlideAnchor[] = [];
    slider.Components.Slides.forEach((s) => {
      if (s?.slide?.querySelector('a')) {
        hrefMap.push({ slideId: s.slide.id, slideHref: s.slide.querySelector('a').getAttribute('href') });
      }
    });
    return hrefMap;
  };

  private static removeHrefOfInvisibleCardCopies = (slides: any) => {
    slides.forEach((s) => {
      //slide != -1 means the card is a clone and is invisible
      if (s.slideIndex != -1) {
        s.slide.querySelectorAll('a').forEach((el) => el.removeAttribute('href'));
      }
    });
  };
  private static updateHrefOfVisibleCards = (slides: any, hrefMap: SlideAnchor[]) => {
    slides.forEach((s) => {
      //slide == -1 means the card is visible
      if (s.slideIndex == -1) {
        this.updateHrefInAnchors(s.slide, hrefMap);
      }
    });
  };

  private static updateHrefInAnchors = (slide: any, hrefMap: SlideAnchor[]) => {
    const slideId = slide.id;
    if (hrefMap.findIndex((x) => x.slideId === slideId) > -1) {
      const slideAnchorElements = slide.querySelectorAll('a');
      slideAnchorElements.forEach((el) => (el.href = hrefMap.find((x) => x.slideId === slideId).slideHref));
    }
  };
}
export interface SlideAnchor {
  slideId: string;
  slideHref: string;
}
